/**
 * Mana math utilities for deck building analysis.
 *
 * Replaces static Karsten lookup tables with hypergeometric probability calculations
 * to support dynamic turn-based mana source recommendations.
 */
import type { DeckFormat } from '@/types/deck';

// ============================================================================
// Core Math
// ============================================================================

/**
 * Compute log(n!) using Stirling's approximation for large n,
 * or direct summation for small n. Uses log-space to avoid overflow.
 */
function logFactorial(n: number): number {
  if (n <= 1) return 0;
  // For small n, compute directly
  if (n <= 20) {
    let result = 0;
    for (let i = 2; i <= n; i++) {
      result += Math.log(i);
    }
    return result;
  }
  // Stirling's approximation for larger values
  return n * Math.log(n) - n + 0.5 * Math.log(2 * Math.PI * n) + 1 / (12 * n);
}

/**
 * Compute log(C(n, k)) = log(n!) - log(k!) - log((n-k)!)
 */
function logCombination(n: number, k: number): number {
  if (k < 0 || k > n) return -Infinity;
  if (k === 0 || k === n) return 0;
  return logFactorial(n) - logFactorial(k) - logFactorial(n - k);
}

/**
 * Hypergeometric probability: P(X = x) where X ~ Hypergeometric(N, K, n)
 *
 * @param N - Population size (deck size)
 * @param K - Number of success states in population (sources)
 * @param n - Number of draws (cards seen)
 * @param x - Number of observed successes
 */
function hypergeometricPMF(N: number, K: number, n: number, x: number): number {
  if (x < 0 || x > Math.min(K, n) || x < Math.max(0, n - (N - K))) return 0;
  const logP = logCombination(K, x) + logCombination(N - K, n - x) - logCombination(N, n);
  return Math.exp(logP);
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Calculate P(X >= need) using the hypergeometric distribution.
 *
 * "What is the probability of drawing at least `need` sources of a color,
 * given `sources` in a deck of `deckSize`, having seen `cardsSeen` cards?"
 *
 * @param deckSize - Total cards in deck (e.g. 60, 99)
 * @param sources - Number of sources that produce the desired color
 * @param cardsSeen - Number of cards seen by the target turn (opening hand + draws)
 * @param need - Minimum number of sources needed (pips in casting cost)
 */
export function hypergeometricAtLeast(deckSize: number, sources: number, cardsSeen: number, need: number): number {
  if (need <= 0) return 1;
  if (sources <= 0) return 0;
  if (cardsSeen <= 0) return 0;

  // Clamp to valid ranges
  const N = Math.max(1, deckSize);
  const K = Math.min(sources, N);
  const n = Math.min(cardsSeen, N);

  // P(X >= need) = 1 - P(X < need) = 1 - sum(P(X=0) + ... + P(X=need-1))
  let cumulativeLess = 0;
  for (let x = 0; x < need; x++) {
    cumulativeLess += hypergeometricPMF(N, K, n, x);
  }

  return Math.max(0, Math.min(1, 1 - cumulativeLess));
}

/**
 * Find the minimum number of sources needed to achieve P >= targetProbability
 * of drawing at least `need` of them by the given number of `cardsSeen`.
 *
 * Uses binary search over possible source counts.
 *
 * @param deckSize - Total cards in deck
 * @param cardsSeen - Cards seen by target turn
 * @param need - Pips required (e.g. 1 for "C", 2 for "CC")
 * @param targetProbability - Desired reliability (default 0.90 — Karsten uses ~90% for untapped sources)
 */
export function minimumSourcesForTurn(deckSize: number, cardsSeen: number, need: number, targetProbability: number = 0.9): number {
  if (need <= 0) return 0;

  const N = Math.max(1, deckSize);
  const n = Math.min(cardsSeen, N);

  // Binary search: find smallest K where P(X >= need | N, K, n) >= targetProbability
  let lo = need;
  let hi = N;

  // Edge case: even with all cards as sources, can't reach target
  if (hypergeometricAtLeast(N, hi, n, need) < targetProbability) {
    return hi;
  }

  while (lo < hi) {
    const mid = Math.floor((lo + hi) / 2);
    const prob = hypergeometricAtLeast(N, mid, n, need);
    if (prob >= targetProbability) {
      hi = mid;
    } else {
      lo = mid + 1;
    }
  }

  return lo;
}

/**
 * Number of cards seen by a given turn (on the play).
 * Opening hand = 7, then draw 1 per turn starting turn 2.
 * Turn 1: 7 cards, Turn 2: 8, Turn 3: 9, etc.
 */
export function cardsByTurn(turn: number): number {
  if (turn <= 0) return 0;
  return 6 + turn; // 7 cards in hand + (turn - 1) draws = 6 + turn
}

/**
 * Get the default target turn for a given deck format.
 * 60-card formats default to T4, Commander/multiplayer to T6.
 */
export function getDefaultTurn(format: DeckFormat | null): number {
  switch (format) {
    case 'commander':
    case 'brawl':
    case 'oathbreaker':
      return 6;
    case 'vintage':
    case 'legacy':
      return 3;
    default:
      return 4;
  }
}
